import { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Typography, Button, Divider, Spin, Alert, Space, Tooltip, Timeline,
} from 'antd';
import {
  ArrowLeftOutlined, UserOutlined, FileTextOutlined, CreditCardOutlined,
  ShoppingCartOutlined, PaperClipOutlined, FilePdfOutlined, LinkOutlined, HistoryOutlined,
} from '@ant-design/icons';
import { api } from '../../services/api';
import { SectionCard } from '../../shared/components/SectionCard';
import { useClient } from '../../context/ClientContext';
import { EstadoPedidoChip } from '../../shared/components/EstadoPedidoChip';
import { EstadoPagoChip } from '../../shared/components/EstadoPagoChip';

const { Title, Text } = Typography;

const fmt = (iso) =>
  iso ? new Date(iso).toLocaleString('es-AR', { dateStyle: 'short', timeStyle: 'short' }) : '—';

export const PedidoDetalleAdmin = () => {
  const { id } = useParams();
  const { clientId, theme , clientPath} = useClient();
  const primary = theme?.primaryColor || '#0f172a';
  const navigate = useNavigate();

  const [pedido, setPedido]     = useState(null);
  const [loading, setLoading]   = useState(true);
  const [error, setError]       = useState(null);
  const [blobUrls, setBlobUrls] = useState({});
  const [historial, setHistorial] = useState([]);

  useEffect(() => {
    const cargar = async () => {
      setLoading(true);
      try {
        const res = await api.admin.getPedidoDetalle(id);
        if (res.status === 'success') setPedido(res.data);
        else setError(res.message || 'Error al cargar el pedido');
      } catch { setError('Error al conectar con el servidor'); }
      finally { setLoading(false); }
    };
    cargar();
    // El historial es aparte: si falla, el pedido se ve igual.
    api.admin.getHistorialPedido(id)
      .then((res) => res.status === 'success' && setHistorial(res.data || []))
      .catch(() => {});
  }, [id]);

  useEffect(() => {
    if (!pedido?.adjuntos?.length) return;
    pedido.adjuntos.forEach(async (adj) => {
      if (blobUrls[adj.id]) return;
      const url = await api.pagos.getAdjuntoBlob(adj.id);
      if (url) setBlobUrls(prev => ({ ...prev, [adj.id]: url }));
    });
  }, [pedido]); // eslint-disable-line

  if (loading) return <div style={{ textAlign: 'center', padding: 48 }}><Spin size="large" /></div>;
  if (error)   return <Alert type="error" message={error} />;
  if (!pedido) return null;

  const esPdf = (n) => n?.toLowerCase().endsWith('.pdf');

  return (
    <div style={{ maxWidth: 860, margin: '0 auto' }}>
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 24 }}>
        <Button icon={<ArrowLeftOutlined />} type="text" style={{ marginLeft: -8 }} onClick={() => navigate(clientPath('/admin/pedidos'))} />
        <Text strong style={{ fontSize: 28, lineHeight: 1.2 }}>Pedido #{pedido.id}</Text>
        <EstadoPedidoChip nombre={pedido.estado_nombre} descripcion={pedido.estado_desc} color={pedido.estado_color} />
      </div>

      <Space direction="vertical" size={16} style={{ width: '100%' }}>
        {/* Cliente */}
        <SectionCard icon={<UserOutlined />} title="Cliente">
          <InfoRow label="Nombre" value={pedido.nombre_cliente || '—'} />
          <InfoRow label="Email"  value={pedido.email_cliente  || '—'} />
        </SectionCard>

        {/* Pedido */}
        <SectionCard icon={<FileTextOutlined />} title="Pedido">
          <InfoRow label="Fecha"  value={fmt(pedido.fecha_alta)} />
          {pedido.cargado_por && <InfoRow label="Cargado por" value={pedido.cargado_por} />}
          <InfoRow label="Total"  value={`$ ${Number(pedido.total).toFixed(2)} ${pedido.currency || ''}`} bold />
          <InfoRow label="Estado" value={<EstadoPedidoChip nombre={pedido.estado_nombre} descripcion={pedido.estado_desc} color={pedido.estado_color} />} />
        </SectionCard>

        {/* Pago */}
        <SectionCard icon={<CreditCardOutlined />} title="Pago">
          <InfoRow label="Método"      value={pedido.metodo_pago || '—'} />
          <InfoRow label="Estado pago" value={<EstadoPagoChip nombre={pedido.estado_pago_nombre} descripcion={pedido.estado_pago_desc} color={pedido.estado_pago_color} />} />
          {pedido.payment?.currency_paid && pedido.payment.currency_paid !== pedido.payment.currency && (
            <InfoRow label={`Cobrado en ${pedido.payment.currency_paid}`} value={enMoneda(pedido.payment)} />
          )}
          {pedido.payment?.created_at && <InfoRow label="Registrado"  value={fmt(pedido.payment.created_at)} />}
          {pedido.payment?.aprobado && <InfoRow label="Aprobado" value={decision(pedido.payment.aprobado, pedido.aprobado_por)} />}
          {pedido.payment?.rechazado && <InfoRow label="Rechazado" value={decision(pedido.payment.rechazado, pedido.rechazado_por)} />}
          {pedido.payment?.updated_at && <InfoRow label="Actualizado" value={fmt(pedido.payment.updated_at)} />}
        </SectionCard>

        {/* Items */}
        <SectionCard icon={<ShoppingCartOutlined />} title={`Productos (${(pedido.items || []).length})`}>
          {(pedido.items || []).length === 0 ? (
            <Text type="secondary">Sin items</Text>
          ) : (
            <>
              {pedido.items.map((item, i) => (
                <div key={i} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '8px 0', borderBottom: i < pedido.items.length - 1 ? '1px solid #f0f0f0' : 'none' }}>
                  <div>
                    <div style={{ fontWeight: 500 }}>{item.NombreProducto || item.nombre_producto || '—'}</div>
                    {(item.CodigoProducto || item.codigo_producto) && (
                      <Text type="secondary" style={{ fontSize: 12 }}>CÓD. {item.CodigoProducto || item.codigo_producto}</Text>
                    )}
                  </div>
                  <div style={{ textAlign: 'right', marginLeft: 16 }}>
                    <div style={{ fontSize: 13 }}>{item.Cantidad || item.cantidad} u. × $ {Number(item.Precio || item.precio).toFixed(2)}</div>
                    <div style={{ fontWeight: 600 }}>$ {Number((item.Cantidad || item.cantidad) * (item.Precio || item.precio)).toFixed(2)}</div>
                  </div>
                </div>
              ))}
              <Divider style={{ margin: '8px 0' }} />
              <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13 }}>
                <Text type="secondary">Subtotal</Text>
                <Text type="secondary">$ {Number(pedido.subtotal || 0).toFixed(2)}</Text>
              </div>
              {(pedido.promos || []).map((p, i) => (
                <div key={i} style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13 }}>
                  <Text type="secondary">{p.nombre}</Text>
                  <Text type="secondary">- $ {Number(p.descuento).toFixed(2)}</Text>
                </div>
              ))}
              {Number(pedido.ajuste_pago || 0) !== 0 && (
                <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13 }}>
                  <Text type="secondary">Ajuste por medio de pago</Text>
                  <Text type="secondary">$ {Number(pedido.ajuste_pago).toFixed(2)}</Text>
                </div>
              )}
              <div style={{ textAlign: 'right', fontWeight: 700, marginTop: 6 }}>Total: $ {Number(pedido.total).toFixed(2)}</div>
            </>
          )}
        </SectionCard>

        {/* Adjuntos */}
        {(pedido.adjuntos || []).length > 0 && (
          <SectionCard icon={<PaperClipOutlined />} title={`Comprobantes (${pedido.adjuntos.length})`}>
            <Space direction="vertical" size={12} style={{ width: '100%' }}>
              {pedido.adjuntos.map((adj) => {
                const url = blobUrls[adj.id];
                const pdf = esPdf(adj.adjunto);
                return (
                  <div key={adj.id} style={{ border: '1px solid #e8e8e8', borderRadius: 8, overflow: 'hidden' }}>
                    {!pdf && url && (
                      <img src={url} alt={adj.adjunto}
                        style={{ width: '100%', maxHeight: 200, objectFit: 'cover', display: 'block', cursor: 'zoom-in' }}
                        onClick={() => window.open(url, '_blank')}
                      />
                    )}
                    <div style={{ padding: '8px 12px', background: '#fafafa', display: 'flex', alignItems: 'center', gap: 8 }}>
                      {pdf && <FilePdfOutlined style={{ color: '#ff4d4f', flexShrink: 0 }} />}
                      <Text ellipsis style={{ flex: 1, fontSize: 12 }}>{adj.adjunto}</Text>
                      <Tooltip title="Abrir en nueva pestaña">
                        <Button type="text" size="small" icon={<LinkOutlined />} disabled={!url}
                          onClick={() => url && window.open(url, '_blank')} />
                      </Tooltip>
                    </div>
                  </div>
                );
              })}
            </Space>
          </SectionCard>
        )}

        {historial.length > 0 && (
          <SectionCard icon={<HistoryOutlined />} title="Historial">
            <Timeline
              style={{ marginTop: 8, marginBottom: -16 }}
              items={historial.map((ev) => ({
                color: primary,
                children: (
                  <div>
                    <Text strong style={{ fontSize: 13 }}>{ev.quien}</Text>
                    <Text type="secondary" style={{ fontSize: 12, marginLeft: 8 }}>{fmt(ev.fecha)}</Text>
                    {(ev.detalle || []).map((d, i) => (
                      <div key={i}><Text type="secondary" style={{ fontSize: 13 }}>{d}</Text></div>
                    ))}
                  </div>
                ),
              }))}
            />
          </SectionCard>
        )}
      </Space>
    </div>
  );
};

// Lo cobrado en otra moneda que la del pedido (PayPal, en dólares), con la
// cotización que quedó congelada. Sin amount_paid todavía no se acreditó.
const enMoneda = (p) => `${p.currency_paid} ${p.amount_paid != null ? Number(p.amount_paid).toFixed(2) : '—'}${p.exchange_rate ? ` · a $ ${Number(p.exchange_rate).toLocaleString('es-AR')}` : ''}`;

// Sin persona es que lo resolvió la pasarela sola (por ejemplo, Mercado Pago).
const decision = (fecha, quien) => `${fmt(fecha)} · ${quien || 'la pasarela'}`;

const InfoRow = ({ label, value, bold }) => (
  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '5px 0' }}>
    <Text type="secondary" style={{ fontSize: 13 }}>{label}</Text>
    {typeof value === 'string' || typeof value === 'number'
      ? <Text strong={bold} style={{ fontSize: 13 }}>{value}</Text>
      : value}
  </div>
);
